import React, { FC, useMemo } from "react"
import { graphql, Script, useStaticQuery } from "gatsby"

import { BespokeComponentFragmentFragment, HomeHeroQueryQuery } from "@graphqlTypes"

import { BENTO_BOX_VARIANTS, BentoBoxCard } from "@components/lp/BentoBoxBlock/BentoBoxBlock"
import ImageAboveFold from "@components/ImageAboveFold/ImageAboveFold"
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@components/Button/Button"

import { HomeHeroWrapperDIV, HomeHeroHERO } from "./HomeHero.styles"
declare global {
  interface Window {
    unify: any
  }
}

const UNIFY_API_SCRIPT = `!function(){window.unify||(window.unify=Object.assign([],["identify","page","startAutoPage","stopAutoPage","startAutoIdentify","stopAutoIdentify"].reduce((function(t,e){return t[e]=function(){return unify.push([e,[].slice.call(arguments)]),unify},t}),{})));var t=document.createElement("script");t.async=!0,t.setAttribute("src","https://tag.unifyintent.com/v1/nnStwLc4hpAqP3A3ZP4CY/script.js"),t.setAttribute("data-api-key","wk_L3PnT53J_EggwaMtf7a3BoR7Kafoaets3xdEKDcj5"),t.setAttribute("id","unifytag"),(document.body||document.head).appendChild(t)}();`

type HomeHeroJsonPayload = {
  bentoBoxId: string,
  button1Text: string,
  button1Url: string,
  button2Text: string,
  button2Url: string,
  h1: string,
  quote: string,
  quoteAttribution: string,
}

const HomeHero: FC<{
  contentful_id: BespokeComponentFragmentFragment["contentful_id"],
  jsonPayload: BespokeComponentFragmentFragment["jsonPayload"],
}> = ({
  contentful_id,
  jsonPayload,
}) => {
    const {
      backgroundImage,
      mennTAvatar,
      wrappedBentoBoxBlock,
    } = useStaticQuery<HomeHeroQueryQuery>(graphql`
      query HomeHeroQuery {
        # Wrapped BentoBoxBlock to allow for background styling
        wrappedBentoBoxBlock: contentfulLpBentoBoxBlock(contentful_id:{eq:"20PkVfVnaCai5xAMrY9z6b"}) {
          ...BentoBoxBlockFragment
        }
        mennTAvatar: contentfulAsset(contentful_id: {eq: "4Tj6WMTgNyoW5UB4a5ydch"}) {
          imgixImage {
            gatsbyImageData(width: 32)
          }
        }
        backgroundImage: contentfulAsset(contentful_id: {eq: "3iT1nqgyODiERSoz2hKpHh"}) {
          imgixImage {
            url(imgixParams: {w: 3200, q: 90, auto: "format"})
          }
        }
      }
    `)

    const json = useMemo(() => {
      try {
        return JSON.parse(jsonPayload?.internal.content || "") as HomeHeroJsonPayload
      } catch (e) {
        console.error(`Failed to parse JSON Payload: HomeHero (${contentful_id})`)
        return null
      }
    }, [jsonPayload])

    if (!json) return null

    return (
      <>
        <Script dangerouslySetInnerHTML={{ __html: UNIFY_API_SCRIPT }} />
        <HomeHeroWrapperDIV>
          <HomeHeroHERO>
            <div className="container">
              <div className="grid">
                <h1
                  className="column column--1-9-M font-weight--medium font-size--6XL"
                  dangerouslySetInnerHTML={{ __html: json.h1 }}
                />
                <div className="column buttons">
                  <Button
                    href={json.button1Url}
                    size={BUTTON_SIZES.L}
                  >
                    {json.button1Text}
                  </Button>
                  <Button
                    className="elevation--XS"
                    href={json.button2Url}
                    size={BUTTON_SIZES.L}
                    variant={BUTTON_VARIANTS.SECONDARY}
                  >
                    {json.button2Text}
                  </Button>
                </div>
              </div>
            </div>
          </HomeHeroHERO>
          <section className={`BentoBoxBlock uid--20PkVfVnaCai5xAMrY9z6b`}>
            <div className="container">
              <div className="flex">
                {wrappedBentoBoxBlock?.items?.map(item => !item ? null : (
                  <BentoBoxCard {...item} variant={BENTO_BOX_VARIANTS.DEFAULT} />
                ))}
              </div>
            </div>
          </section>
          <section className="HomeHero--quote">
            <div className="container">
              <div className="flex">
                <p
                  className="font-family--sans-brand font-weight--medium font-size--XL color--basics-white margin-bottom--0"
                  dangerouslySetInnerHTML={{ __html: json.quote }}
                />
                <div className="quote-attribution">
                  {mennTAvatar?.imgixImage?.gatsbyImageData && (
                    <ImageAboveFold
                      alt="Menn T. Avatar"
                      aspectRatio={1}
                      imageData={mennTAvatar.imgixImage.gatsbyImageData}
                    />
                  )}
                  <span
                    className="type--mono-M color--basics-white margin-bottom--0"
                    dangerouslySetInnerHTML={{ __html: json.quoteAttribution }}
                  />
                </div>
              </div>
            </div>
          </section>
          {backgroundImage?.imgixImage?.url && (
            <div
              className="HomeHero--background"
              style={{
                backgroundImage: `url(${backgroundImage.imgixImage.url})`,
              }}
            />
          )}
        </HomeHeroWrapperDIV>
      </>
    )
  }

export default HomeHero